import React, { useState, useEffect, useCallback } from "react";
import { AppConfig, Backorder, BackorderSort, BackorderType, UpsertBackorder } from "../types";
import { DropCatchService } from "../services/dropcatchService";
import { GeminiService } from "../services/geminiService";

interface BackorderManagerProps {
  config: AppConfig;
  dropcatchService: DropCatchService;
  geminiService: GeminiService;
}

const BackorderManager: React.FC<BackorderManagerProps> = ({
  config,
  dropcatchService,
  geminiService,
}) => {
  const [backorders, setBackorders] = useState<Backorder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<BackorderSort>("DropDateAsc");
  const [selectedBackorders, setSelectedBackorders] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDomains, setNewDomains] = useState("");
  const [newType, setNewType] = useState<BackorderType>("Standard");
  const [newMaxBid, setNewMaxBid] = useState("");
  const [creating, setCreating] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const loadBackorders = useCallback(async () => {
    if (!dropcatchService.isConfigured()) {
      setError("ERROR: DROPCATCH API NOT CONFIGURED");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await dropcatchService.getBackorders({
        searchTerm: searchTerm || undefined,
        sort: sortBy,
        size: 50,
      });
      setBackorders(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "FAILED TO LOAD BACKORDERS");
    } finally {
      setLoading(false);
    }
  }, [dropcatchService, searchTerm, sortBy]);

  useEffect(() => {
    if (dropcatchService.isConfigured()) {
      loadBackorders();
    }
  }, []);

  const handleCreateBackorders = async () => {
    const domains = newDomains
      .split(/[\n,]/)
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d && d.includes("."));

    if (domains.length === 0) {
      setError("ERROR: ENTER VALID DOMAIN NAMES");
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const backordersToCreate: UpsertBackorder[] = domains.map((domain) => ({
        domain: domain,
        type: newType,
        amount: newMaxBid ? parseFloat(newMaxBid) : undefined,
      }));

      const result = await dropcatchService.createBackorders(backordersToCreate);

      if (result.successes?.length) {
        setSuccess(`CREATED ${result.successes.length} BACKORDER(S)`);
        setNewDomains("");
        setNewMaxBid("");
        setShowAddForm(false);
        loadBackorders();
      }

      if (result.failures?.length) {
        setError(
          `FAILED: ${result.failures.length} BACKORDER(S): ${result.failures
            .map((f: { domain: string | null; error: { description: string | null } | null }) => `${f.domain}: ${f.error?.description}`)
            .join(", ")}`
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "FAILED TO CREATE BACKORDERS");
    } finally {
      setCreating(false);
    }
  };

  const handleCancelBackorders = async () => {
    if (selectedBackorders.size === 0) return;

    const domains = Array.from(selectedBackorders);

    try {
      const result = await dropcatchService.cancelBackorders(domains);

      if (result.successes?.length) {
        setSuccess(`CANCELLED ${result.successes.length} BACKORDER(S)`);
        setSelectedBackorders(new Set());
        loadBackorders();
      }

      if (result.failures?.length) {
        setError(`FAILED TO CANCEL SOME BACKORDERS`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "FAILED TO CANCEL BACKORDERS");
    }
  };

  const handleGetSuggestions = async () => {
    if (!geminiService.isConfigured()) {
      setError("ERROR: GOOGLE API KEY NOT CONFIGURED");
      return;
    }

    setLoadingSuggestions(true);
    setShowSuggestions(true);

    try {
      const keywords = config.search_keywords.length > 0
        ? config.search_keywords
        : ["law", "legal", "attorney"];
      const results = await geminiService.suggestDomains(keywords);
      setSuggestions(results);
    } catch (err) {
      setError("FAILED TO GET AI SUGGESTIONS");
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const addSuggestionToForm = (domain: string) => {
    setNewDomains((prev) => (prev ? `${prev}\n${domain}` : domain));
    setShowAddForm(true);
  };

  const toggleSelect = (domain: string) => {
    setSelectedBackorders((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  const formatDropDate = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = d.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days < 0) return "DROPPED";
    if (days === 0) return "TODAY";
    if (days === 1) return "TOMORROW";
    return `${days}D`;
  };

  return (
    <div className="p-6 space-y-6 bg-black min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-green-400 tracking-wider">
            {">"} BACKORDER MANAGER
          </h1>
          <p className="text-green-600 text-sm mt-1">
            DOMAIN BACKORDER MANAGEMENT MODULE
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleGetSuggestions}
            className="px-4 py-2 border border-green-700 text-green-500 hover:border-green-500 hover:bg-green-950 transition-colors text-sm tracking-wider"
          >
            [AI SUGGEST]
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-green-500 text-black font-bold hover:bg-green-400 transition-all tracking-wider text-sm"
            style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.3)" }}
          >
            [ADD NEW]
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="border-2 border-green-500 bg-black p-4 animate-pulse">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-400 font-bold">*** ERROR ***</p>
              <p className="text-green-500 text-sm mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-green-600 hover:text-green-400">[X]</button>
          </div>
        </div>
      )}

      {success && (
        <div className="border-2 border-green-500 bg-black p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-400 font-bold">*** SUCCESS ***</p>
              <p className="text-green-500 text-sm mt-1">{success}</p>
            </div>
            <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-400">[X]</button>
          </div>
        </div>
      )}

      {/* AI Suggestions Panel */}
      {showSuggestions && (
        <div className="border-2 border-green-500 bg-black p-4" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-green-400 tracking-wider">{">>>"} AI DOMAIN SUGGESTIONS</h3>
            <button onClick={() => setShowSuggestions(false)} className="text-green-600 hover:text-green-400">[X]</button>
          </div>

          {loadingSuggestions ? (
            <div className="py-8 text-center text-green-500 animate-pulse">GENERATING SUGGESTIONS...</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {suggestions.map((domain) => (
                <button
                  key={domain}
                  onClick={() => addSuggestionToForm(domain)}
                  className="px-3 py-1 border border-green-700 text-green-500 text-sm hover:border-green-500 hover:bg-green-950 transition-colors"
                >
                  {domain}
                </button>
              ))}
              {suggestions.length === 0 && !loadingSuggestions && (
                <p className="text-green-600">NO SUGGESTIONS. CLICK AI SUGGEST TO GENERATE.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Backorder Form */}
      {showAddForm && (
        <div className="border-2 border-green-500 bg-black p-4" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-green-400 tracking-wider">{">>>"} ADD NEW BACKORDERS</h3>
            <button onClick={() => setShowAddForm(false)} className="text-green-600 hover:text-green-400">[X]</button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs text-green-600 mb-1 tracking-wider">DOMAIN_LIST (ONE PER LINE):</label>
              <textarea
                value={newDomains}
                onChange={(e) => setNewDomains(e.target.value)}
                placeholder="example.com&#10;another-domain.io"
                className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none h-32 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-green-600 mb-1 tracking-wider">BACKORDER_TYPE:</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as BackorderType)}
                  className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                >
                  <option value="Standard">STANDARD</option>
                  <option value="DiscountClub">DISCOUNT_CLUB</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-green-600 mb-1 tracking-wider">MAX_BID (OPTIONAL):</label>
                <input
                  type="number"
                  value={newMaxBid}
                  onChange={(e) => setNewMaxBid(e.target.value)}
                  placeholder="$0"
                  className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-green-700 text-green-500 hover:border-green-500 transition-colors text-sm tracking-wider"
              >
                [CANCEL]
              </button>
              <button
                onClick={handleCreateBackorders}
                disabled={creating || !newDomains.trim()}
                className="px-4 py-2 bg-green-500 text-black font-bold hover:bg-green-400 transition-colors disabled:opacity-50 text-sm tracking-wider"
              >
                {creating ? "[CREATING...]" : "[CREATE]"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="border-2 border-green-500 bg-black p-4" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="SEARCH BACKORDERS..."
              className="w-full bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
            />
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as BackorderSort)}
            className="bg-black border-2 border-green-700 focus:border-green-500 px-3 py-2 text-green-400 outline-none"
          >
            <option value="DropDateAsc">DROP_DATE (SOONEST)</option>
            <option value="DropDateDesc">DROP_DATE (LATEST)</option>
            <option value="DomainAsc">DOMAIN A-Z</option>
            <option value="MaxBidDesc">MAX_BID (HIGH-LOW)</option>
          </select>

          <button
            onClick={loadBackorders}
            disabled={loading}
            className="px-4 py-2 border border-green-700 text-green-500 hover:border-green-500 hover:bg-green-950 transition-colors text-sm tracking-wider"
          >
            {loading ? "[LOADING...]" : "[REFRESH]"}
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedBackorders.size > 0 && (
        <div className="border-2 border-green-500 bg-black p-4 flex items-center justify-between">
          <span className="text-green-500">
            SELECTED: <span className="text-green-400 font-bold">{selectedBackorders.size}</span> BACKORDER(S)
          </span>
          <button
            onClick={handleCancelBackorders}
            className="px-4 py-2 border border-green-500 text-green-500 hover:bg-green-500 hover:text-black transition-colors text-sm tracking-wider"
          >
            [CANCEL SELECTED]
          </button>
        </div>
      )}

      {/* Backorders Table */}
      <div className="border-2 border-green-500 bg-black" style={{ boxShadow: "0 0 15px rgba(34, 197, 94, 0.2)" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b-2 border-green-500 bg-green-950/30">
                <th className="px-4 py-3 text-xs font-bold text-green-400 tracking-wider">
                  <div className="w-4 h-4 border border-green-600" />
                </th>
                <th className="px-4 py-3 text-xs font-bold text-green-400 tracking-wider">DOMAIN</th>
                <th className="px-4 py-3 text-xs font-bold text-green-400 tracking-wider">TYPE</th>
                <th className="px-4 py-3 text-xs font-bold text-green-400 tracking-wider">DROP_DATE</th>
                <th className="px-4 py-3 text-xs font-bold text-green-400 tracking-wider">MAX_BID</th>
              </tr>
            </thead>
            <tbody>
              {backorders.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <pre className="text-green-500 text-xs mb-4">
{`
   _________________
  |                 |
  |  NO BACKORDERS  |
  |  FOUND          |
  |_________________|
`}
                    </pre>
                    <p className="text-green-600 text-sm">CLICK [ADD NEW] TO CREATE BACKORDERS</p>
                  </td>
                </tr>
              ) : (
                backorders.map((bo, index) => (
                  <tr key={bo.domain} className="border-b border-green-900 hover:bg-green-950/30 transition-colors">
                    <td className="px-4 py-3">
                      <div
                        className={`w-4 h-4 border cursor-pointer ${
                          selectedBackorders.has(bo.domain || "")
                            ? "bg-green-500 border-green-500"
                            : "border-green-600"
                        } flex items-center justify-center`}
                        onClick={() => toggleSelect(bo.domain || "")}
                      >
                        {selectedBackorders.has(bo.domain || "") && <span className="text-black text-xs">X</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-green-700 text-xs mr-2">{String(index + 1).padStart(2, "0")}.</span>
                      <span className="font-bold text-green-400">{bo.domain}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs border ${
                        bo.type === "DiscountClub"
                          ? "border-green-400 text-green-400"
                          : "border-green-600 text-green-600"
                      }`}>
                        {bo.type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-green-400">{new Date(bo.dropDate).toLocaleDateString()}</span>
                        <span className="text-xs text-green-600">{formatDropDate(bo.dropDate)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {bo.maxBid ? (
                        <span className="font-semibold text-green-400">${bo.maxBid}</span>
                      ) : (
                        <span className="text-green-800">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Status */}
      <div className="border-t-2 border-green-500 pt-4 text-xs text-green-600 flex justify-between">
        <span>TOTAL: {backorders.length} BACKORDERS</span>
        <span className="animate-pulse">&gt;_</span>
      </div>
    </div>
  );
};

export default BackorderManager;
